import { makeStyles, mergeClasses } from '@fluentui/react-components';
import type { ReactNode } from 'react';

import { palette, tokens } from '../../theme';

export type FilterPillSize = 'sm' | 'md';

export type FilterPillGroupProps = {
    size?: FilterPillSize;
    children: ReactNode;
};

// The app's filter control: a soft pill tray holding one or more segments, the
// selected one filled with the accent. Shared by the report toolbar and the
// pickers' filters so they stay identical.
export const FilterPillGroup: React.FC<FilterPillGroupProps> = ({ size = 'md', children }) => {
    const classes = useStyles();
    return <div className={mergeClasses(classes.group, size === 'sm' && classes.groupSm)}>{children}</div>;
};

export type FilterPillButtonProps = {
    active: boolean;
    size?: FilterPillSize;
    onClick: () => void;
    children: ReactNode;
};

export const FilterPillButton: React.FC<FilterPillButtonProps> = ({ active, size = 'md', onClick, children }) => {
    const classes = useStyles();
    return (
        <button
            type="button"
            aria-pressed={active}
            className={mergeClasses(classes.btn, size === 'sm' && classes.btnSm, active && classes.btnActive)}
            onClick={onClick}
        >
            {children}
        </button>
    );
};

const useStyles = makeStyles({
    group: {
        display: 'inline-flex',
        gap: '4px',
        backgroundColor: palette.panelBg,
        boxShadow: palette.softShadowSm,
        padding: '4px',
        borderRadius: palette.pillRadius,
    },
    groupSm: {
        gap: '3px',
        padding: '3px',
    },
    btn: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        border: 'none',
        background: 'transparent',
        height: '30px',
        padding: '0 14px',
        fontFamily: 'inherit',
        fontSize: '12.5px',
        fontWeight: tokens.fontWeightSemibold,
        letterSpacing: 'normal',
        textTransform: 'none',
        whiteSpace: 'nowrap',
        color: tokens.colorNeutralForeground2,
        cursor: 'pointer',
        borderRadius: palette.pillRadius,
        transition: 'background-color 160ms ease, color 160ms ease',
        '&:hover': {
            backgroundColor: palette.accentBg,
            color: palette.accentText,
        },
    },
    btnSm: {
        gap: '5px',
        height: '26px',
        padding: '0 11px',
        fontSize: '11.5px',
    },
    btnActive: {
        backgroundColor: palette.accent,
        color: palette.primaryText,
        '&:hover': {
            backgroundColor: palette.accentHover,
            color: palette.primaryText,
        },
    },
});
